import { useState, useCallback } from 'react'
import { ocrService } from '../services/ocrService'

/**
 * Hook for OCR functionality
 * Handles API calls and result processing. Image identifier set matches /getImage params.
 */
export const useOCR = () => {
  const [isProcessing, setIsProcessing] = useState(false)
  const [extractedData, setExtractedData] = useState({})
  const [rawText, setRawText] = useState('')
  const [error, setError] = useState(null)

  /**
   * Run OCR on an image. Backend fetches image by IDs, runs OCR, returns extracted data.
   * @param {object} params - { objectId, objectImageId, objectImageEditId, filenameExtension, isUploaded, type, satsueiMemoCategoryId }
   * @returns {Promise<object>} { extractedData, rawText }
   */
  const recognizeImage = useCallback(async ({ objectId, objectImageId, objectImageEditId, filenameExtension, isUploaded, type, satsueiMemoCategoryId }) => {
    setIsProcessing(true)
    setError(null)
    setExtractedData({})
    setRawText('')

    try {
      const result = await ocrService.recognizeImage({
        objectId,
        objectImageId,
        objectImageEditId,
        filenameExtension,
        isUploaded,
        type,
        satsueiMemoCategoryId
      })

      if (result.returnCode !== '200') {
        throw new Error(result.message || 'OCRに失敗しました')
      }

      setExtractedData(result.extractedData || {})
      setRawText(result.rawText || '')

      return {
        extractedData: result.extractedData,
        rawText: result.rawText
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'OCRに失敗しました'
      setError(errorMessage)
      throw err
    } finally {
      setIsProcessing(false)
    }
  }, [])

  /**
   * Clear OCR results
   */
  const clearResults = useCallback(() => {
    setExtractedData({})
    setRawText('')
    setError(null)
  }, [])

  return {
    isProcessing,
    extractedData,
    rawText,
    error,
    recognizeImage,
    clearResults
  }
}

export default useOCR
